import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getSessionTenantId } from '@/lib/auth-helpers';
import { supabase } from '@/lib/supabase';
import type { CampaignInsert, CampaignUpdate } from '../types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CampaignInsert) => {
      const tenantId = await getSessionTenantId();
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await db
        .from('campaigns')
        .insert({ ...payload, tenant_id: tenantId, created_by: user?.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Campanha criada!');
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (e: Error) => toast.error(`Erro ao criar campanha: ${e.message}`),
  });
}

export function useUpdateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: CampaignUpdate }) => {
      const { data, error } = await db
        .from('campaigns')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_: unknown, { id }: { id: string; payload: CampaignUpdate }) => {
      toast.success('Campanha atualizada!');
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
      void qc.invalidateQueries({ queryKey: ['campaign', id] });
    },
    onError: (e: Error) => toast.error(`Erro ao atualizar: ${e.message}`),
  });
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db.from('campaigns').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Campanha removida.');
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (e: Error) => toast.error(`Erro ao remover: ${e.message}`),
  });
}

/** Muda status: draft → scheduled | running | paused | completed */
export function useUpdateCampaignStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const extras: Record<string, string> = {};
      if (status === 'running')   extras['started_at']   = new Date().toISOString();
      if (status === 'completed') extras['completed_at'] = new Date().toISOString();
      const { data, error } = await db
        .from('campaigns')
        .update({ status, ...extras })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_: unknown, { id, status }: { id: string; status: string }) => {
      const labels: Record<string, string> = {
        scheduled: 'Campanha agendada!',
        running:   'Disparo iniciado!',
        paused:    'Campanha pausada.',
        completed: 'Campanha concluída!',
      };
      toast.success(labels[status] ?? 'Status atualizado.');
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
      void qc.invalidateQueries({ queryKey: ['campaign', id] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
}
